
import { CampingSite, SiteAmenities, FrontendAmenities } from './types';

/**
 * Transforms database amenities object to frontend-friendly format
 */
export function transformAmenities(
  siteId: string, 
  hasElectricity: boolean, 
  hasWater: boolean, 
  siteAmenities: SiteAmenities
): FrontendAmenities {
  // Add detailed logging for electrical amenities
  console.log(`[TRANSFORM] Electrical details for site ${siteId}:`, {
    hasElectricity,
    amperage: siteAmenities.amperage,
    electric_50amp: siteAmenities.electric_50amp,
    raw: siteAmenities
  });
  
  // For Sheep Creek RV sites, ensure electric50Amp is set properly
  let electric50Amp = siteAmenities.electric_50amp ?? false;
  
  // Handle special case for Sheep Creek RV sites
  if (siteId.includes('sheep-creek') && siteId.toLowerCase().includes('rv')) {
    console.log(`[TRANSFORM] Processing Sheep Creek RV site: ${siteId}`);
    // Override the electric50Amp for Sheep Creek RV sites to true
    if (hasElectricity) {
      electric50Amp = true;
      console.log(`[TRANSFORM] Forcing electric50Amp=true for Sheep Creek RV site: ${siteId}`);
    }
  }
  
  return {
    electric: hasElectricity,
    water: hasWater,
    petsAllowed: siteAmenities.petsAllowed ?? true,
    amperage: siteAmenities.amperage ?? (hasElectricity ? 30 : undefined), // Default to 30AMP for electric sites
    electric50Amp: electric50Amp,
    sewage: siteAmenities.sewage ?? false,
    maxRVLength: siteAmenities.max_rv_length ?? undefined,
    wifi: siteAmenities.wifi ?? false,
    backIn: siteAmenities.back_in ?? false,
    pullThru: siteAmenities.pull_thru ?? false,
    gravelSurface: siteAmenities.gravel_surface ?? false,
    paved: siteAmenities.paved ?? false,
    shade: siteAmenities.shade ?? false,
    shadeMorning: siteAmenities.shade_morning ?? false,
    shadeAfternoon: siteAmenities.shade_afternoon ?? false,
    firePit: siteAmenities.fire_pit ?? false,
    grill: siteAmenities.grill ?? false,
    nearLake: siteAmenities.near_lake ?? false,
    nearRestroom: siteAmenities.near_restroom ?? false,
    picnicTable: siteAmenities.picnic_table ?? false
  };
}

/**
 * Transforms a database site row with amenities into a CampingSite object
 */
export function transformSiteWithAmenities(
  site: any, 
  siteAmenities: SiteAmenities
): CampingSite {
  // Check if we're receiving the expected data
  console.log(`[TRANSFORM] Processing site ${site.site_number}, ID: ${site.id}`);
  console.log(`[TRANSFORM] Raw amenities for site ${site.id}:`, siteAmenities);
  
  // For Sheep Creek sites, add extra debugging
  if (site.location === 'sheep-creek') {
    console.log(`[SHEEP CREEK] Site ${site.site_number} electrical debug:`, {
      hasElectricity: site.has_electricity,
      rawAmperage: siteAmenities.amperage,
      raw50Amp: siteAmenities.electric_50amp,
      siteId: site.id
    });
  }
  
  return {
    id: site.id,
    siteNumber: site.site_number,
    siteType: site.site_type,
    location: site.location,
    hasElectricity: site.has_electricity,
    hasWater: site.has_water,
    description: site.description,
    maxOccupants: site.max_occupants,
    pricePerNight: site.price_per_night,
    active: site.active,
    status: (site.status as any) ?? 'active',
    statusMessage: site.status_message ?? null,
    latitude: site.latitude,
    longitude: site.longitude,
    amenities: transformAmenities(
      site.id, 
      site.has_electricity, 
      site.has_water, 
      siteAmenities
    )
  };
}
